import { meetingConnectJoin, meetingConnectStep } from './meeting-join-chip';
import type { UpcomingMeetingState } from './meeting-row-state';

/**
 * A connect chip's tap (`meeting-connect:<eventId>`, offered by `JoinMeeting` to an owner who has not
 * connected Meetings): the Connectors page's connect, then the join chip's request for that event. The tap is
 * the owner's consent; nothing here runs on its own.
 *
 * The chip's event id is model output, so it is only ever a lookup key: the request is built from the
 * owner's own schedule row. An event that schedule does not hold, or one she cannot join (ended, paused,
 * failed), is `unavailable`: it gets no request, and no connect is written for it. The schedule lists the
 * rows before Meetings is connected, so that check runs before the connect, on both paths.
 *
 * The connect sends NO fields (`MEETING_CONNECT_CHIP_PATCH`). The harness creates a missing connection with the
 * first-connect defaults (REQUESTED_ONLY, talk off) and leaves an existing one exactly as it is, so the connect is
 * safe whatever the read said — another tab or phone may have connected and turned on "join automatically" in
 * the meantime. The fresh read still decides the steps.
 *
 * Import-free with the calls injected, so jest can run it without react-query. Shared 1:1 with iOS
 * `MeetingConnectChipModel` and Android `MeetingConnectChipModel`.
 */

export type MeetingConnectOutcome = 'joined' | 'connectFailed' | 'joinFailed' | 'unavailable';

/** The chip's connect body: nothing, so the server's own first-connect defaults apply and nothing is overwritten. */
export const MEETING_CONNECT_CHIP_PATCH: { joinPolicy?: never; canTalk?: never } = Object.freeze({});

type Row = { calendarEventId: string; startsAt: string; endsAt?: string; state: UpcomingMeetingState };

export interface MeetingConnectDeps<M extends Row> {
  /** The schedule, read fresh: it decides join only, the calendar step, or connect then request. */
  readSchedule: () => Promise<{ connection: unknown; calendars: readonly unknown[]; meetings: readonly M[] }>;
  /** `POST connection` with this body. Throws when it did not connect. */
  connect: (patch: typeof MEETING_CONNECT_CHIP_PATCH) => Promise<void>;
  /** The join chip's Join request. Throws when it failed. */
  decide: (meeting: M) => Promise<void>;
  now: () => number;
  /** A connect this tap started is about to be written. */
  connectStarted: () => void;
  /** The one line a connect this tap started writes when it finishes or fails. */
  connectFinished: (result: { isSuccessful: boolean; loadingTimeMs: number; outcome: MeetingConnectOutcome }) => void;
}

/** The tap. `calendar` means the calendar step must open first; `finishMeetingConnect` runs once it lands. */
export async function startMeetingConnect<M extends Row>(
  eventId: string,
  deps: MeetingConnectDeps<M>,
): Promise<MeetingConnectOutcome | 'calendar'> {
  let schedule;
  try {
    schedule = await deps.readSchedule();
  } catch {
    return 'connectFailed';
  }
  const step = meetingConnectStep(schedule);
  if (step === 'calendar') return 'calendar';
  if (!joinable(eventId, schedule.meetings, deps)) return 'unavailable';
  if (step === 'join') return askIn(eventId, schedule.meetings, deps);
  return connectAndAskIn(eventId, deps);
}

/** The calendar step landed: read the new calendar's schedule, then connect and ask her in. */
export async function finishMeetingConnect<M extends Row>(eventId: string, deps: MeetingConnectDeps<M>): Promise<MeetingConnectOutcome> {
  let schedule;
  try {
    schedule = await deps.readSchedule();
  } catch {
    return 'connectFailed';
  }
  // A schedule that does not show the new calendar yet cannot say the event is unjoinable: nothing is written,
  // and the chip stays for another try.
  if (meetingConnectStep(schedule) === 'calendar') return 'connectFailed';
  if (!joinable(eventId, schedule.meetings, deps)) return 'unavailable';
  return connectAndAskIn(eventId, deps);
}

function joinable<M extends Row>(eventId: string, meetings: readonly M[], deps: MeetingConnectDeps<M>): boolean {
  return meetingConnectJoin(meetings.find((m) => m.calendarEventId === eventId), deps.now()) !== 'unavailable';
}

async function connectAndAskIn<M extends Row>(eventId: string, deps: MeetingConnectDeps<M>): Promise<MeetingConnectOutcome> {
  deps.connectStarted();
  const startedAt = deps.now();
  const finished = (isSuccessful: boolean, outcome: MeetingConnectOutcome) => {
    deps.connectFinished({ isSuccessful, loadingTimeMs: Math.max(0, deps.now() - startedAt), outcome });
    return outcome;
  };
  try {
    await deps.connect(MEETING_CONNECT_CHIP_PATCH);
  } catch {
    return finished(false, 'connectFailed');
  }
  let meetings: readonly M[];
  try {
    meetings = (await deps.readSchedule()).meetings;
  } catch {
    return finished(true, 'joinFailed');
  }
  return finished(true, await askIn(eventId, meetings, deps));
}

async function askIn<M extends Row>(eventId: string, meetings: readonly M[], deps: MeetingConnectDeps<M>): Promise<MeetingConnectOutcome> {
  const meeting = meetings.find((m) => m.calendarEventId === eventId);
  const next = meetingConnectJoin(meeting, deps.now());
  if (next === 'done') return 'joined';
  if (next === 'unavailable' || !meeting) return 'joinFailed';
  try {
    await deps.decide(meeting);
    return 'joined';
  } catch {
    return 'joinFailed';
  }
}
